<script lang="ts">
import { iIcon, iSpinner } from '@inscribe/signal';
import { computed } from 'vue';
import {
  Statuses,
  statusIcon,
  statusIconColor,
  statusBackgroundColour,
  statusTextColour,
} from '@/V2/components/status/utils';

export const StatusBarSizes = {
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'x-large',
};

export default {
  name: 'StatusBar',
  components: {
    iIcon,
    iSpinner,
  },
  props: {
    size: {
      type: String,
      default: StatusBarSizes.MEDIUM,
      validator: (value: string) =>
        Object.values(StatusBarSizes).includes(value),
    },
    state: {
      type: String,
      default: Statuses.NOT_APPLICABLE,
      validator: (value: string) => Object.values(Statuses).includes(value),
    },
    label: {
      type: String,
      default: '',
    },
  },
  computed: {
    cssVars() {
      return {
        '--bar-background-color': statusBackgroundColour(this.state),
        '--bar-text-color': statusTextColour(this.state),
      };
    },
  },
  setup(props) {
    return {
      Statuses,
      StatusBarSizes,
      barIconColor: computed(() => statusIconColor(props.state)),
      barIcon: computed(() => statusIcon(props.state, 'medium')),
      badgeIcon: computed(() => statusIcon(props.state)),
    };
  },
};
</script>

<template>
  <div
    :class="{
      'i-px-6 i-py-5': size === StatusBarSizes.MEDIUM,
      'i-px-8 i-py-6': [StatusBarSizes.LARGE, StatusBarSizes.XLARGE].includes(
        size,
      ),
      'i-radius-lg': size === StatusBarSizes.MEDIUM,
      'i-radius-xl': size === StatusBarSizes.LARGE,
      'i-radius-2xl': size === StatusBarSizes.XLARGE,
    }"
    class="status-bar d-flex align-items-center i-gap-6"
    :style="cssVars"
  >
    <i-spinner v-if="state === Statuses.LOADING" size="sm" />
    <i-icon v-else :symbol="barIcon" :color="barIconColor" :size="16"></i-icon>
    <div
      class="label"
      :class="{
        'i-text-button-3': [
          StatusBarSizes.MEDIUM,
          StatusBarSizes.LARGE,
        ].includes(size),
        'i-text-button-2': size === StatusBarSizes.XLARGE,
      }"
    >
      {{ label }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.status-bar {
  width: 100%;
  background-color: var(--bar-background-color);
  user-select: none;

  .i-spinner {
    padding: 0;
  }

  .label {
    line-height: 16px;
    color: var(--bar-text-color);
  }
}
</style>
